import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, ArrowRight, Zap, Phone } from 'lucide-react';
import LiveDate from '../components/LiveDate';
import './ServicePage.css';

const FAQItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      className={`faq-item ${open ? 'open' : ''}`}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07 }}
      viewport={{ once: true }}
    >
      <button className="faq-question" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <ChevronDown size={16} className={`faq-icon ${open ? 'rotated' : ''}`} />
      </button>
      {open && (
        <motion.div className="faq-answer" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}>
          <p>{a}</p>
        </motion.div>
      )}
    </motion.div>
  );
};

const ServicePage = ({
  acronym,
  title,
  tagline,
  color = '#00d4ff',
  icon = '⚡',
  heroDesc,
  whatIs,
  howItWorks,
  benefits,
  features,
  faqs,
  keralaCities,
  metaTitle,
  metaDesc,
  relatedServices = [],
}) => {
  const schemaFAQ = faqs ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  } : null;

  return (
    <>
      <Helmet>
        <title>{metaTitle || `${acronym} – ${title} Kerala | Rajesh R`}</title>
        <meta name="description" content={metaDesc || `Professional ${title} services in Kerala. ${tagline} Contact Rajesh R at +91 7907038984.`} />
        <meta name="keywords" content={`${acronym} Kerala, ${title.toLowerCase()}, AI optimization Kerala, ${keralaCities?.slice(0,3).join(', ')}`} />
        {schemaFAQ && <script type="application/ld+json">{JSON.stringify(schemaFAQ)}</script>}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": title,
          "provider": {
            "@type": "Person",
            "name": "Rajesh R",
            "telephone": "+917907038984",
            "url": "https://rajeshrnair.com"
          },
          "areaServed": keralaCities || ["Kerala"],
          "description": heroDesc,
          "serviceType": acronym
        })}</script>
      </Helmet>

      {/* HERO */}
      <section className="sp-hero grid-bg">
        <div className="sp-glow" style={{ background: `radial-gradient(circle, ${color}20 0%, transparent 70%)` }}></div>
        <div className="container sp-hero-inner">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <div className="sp-live-bar">
              <span className="live-dot"></span>
              <span>Live: </span>
              <LiveDate showTime className="hero-live-date" />
            </div>
            <div className="sp-acronym-badge" style={{ background: `${color}18`, border: `1px solid ${color}30`, color }}>
              {icon} {acronym}
            </div>
            <h1 className="sp-title">{title}</h1>
            <p className="sp-tagline">{tagline}</p>
            <p className="sp-desc">{heroDesc}</p>
            <div className="sp-ctas">
              <a href={`https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20just%20read%20about%20${encodeURIComponent(acronym)}%20(${encodeURIComponent(title)})%20on%20your%20website.%20I%E2%80%99d%20like%20a%20free%20${encodeURIComponent(acronym)}%20audit%20for%20my%20business%20in%20Kerala.%20Can%20we%20connect%3F`}
                target="_blank" rel="noreferrer" className="btn-primary">
                <Zap size={16} /> Get Free {acronym} Audit
              </a>
              <a href="tel:+917907038984" className="btn-secondary">
                <Phone size={16} /> Call: +91 79070 38984
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT IS */}
      <section className="sp-section">
        <div className="container">
          <div className="sp-what-grid">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="badge" style={{ marginBottom: '16px' }}>What is {acronym}?</div>
              <h2 className="section-title">Understanding <span className="gradient-text">{acronym}</span></h2>
              <div className="sp-what-content">
                {whatIs.map((para, i) => <p key={i}>{para}</p>)}
              </div>
            </motion.div>
            <motion.div className="sp-highlight-box" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="sp-highlight-icon" style={{ color }}>{icon}</div>
              <h3>{acronym} at a Glance</h3>
              <ul className="sp-glance-list">
                {features?.map(f => (
                  <li key={f}><Check size={14} color={color} /> {f}</li>
                ))}
              </ul>
              <div className="sp-platform-note">
                <span className="live-dot"></span>
                <span>Platforms monitored daily as of <LiveDate /></span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      {howItWorks && (
        <section className="sp-section sp-section-alt">
          <div className="container">
            <motion.div className="section-header" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="badge">Process</div>
              <h2 className="section-title" style={{ marginTop: '12px' }}>
                How <span className="gradient-text">{acronym}</span> Works
              </h2>
            </motion.div>
            <div className="sp-steps">
              {howItWorks.map((step, i) => (
                <motion.div
                  key={i}
                  className="sp-step"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="sp-step-num" style={{ background: `${color}20`, color }}>{String(i + 1).padStart(2, '0')}</div>
                  <div>
                    <h3 className="sp-step-title">{step.title}</h3>
                    <p className="sp-step-desc">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BENEFITS */}
      {benefits && (
        <section className="sp-section">
          <div className="container">
            <motion.div className="section-header" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="badge">Benefits</div>
              <h2 className="section-title" style={{ marginTop: '12px' }}>
                Why You Need <span className="gradient-text">{acronym}</span>
              </h2>
            </motion.div>
            <div className="sp-benefits-grid">
              {benefits.map((b, i) => (
                <motion.div
                  key={i}
                  className="sp-benefit-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  viewport={{ once: true }}
                >
                  <div className="sp-benefit-icon" style={{ color }}>{b.icon}</div>
                  <h3 className="sp-benefit-title">{b.title}</h3>
                  <p className="sp-benefit-desc">{b.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* KERALA CITIES */}
      {keralaCities && (
        <section className="sp-section sp-section-alt">
          <div className="container">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="badge">Service Area</div>
              <h2 className="section-title" style={{ marginTop: '12px' }}>
                {acronym} Services Across <span className="gradient-text">Kerala</span>
              </h2>
              <p className="section-subtitle">
                We provide {title} services across all 14 districts of Kerala, helping local businesses get found in AI search engines.
              </p>
              <div className="kerala-cities-grid">
                {keralaCities.map(city => (
                  <div key={city} className="kerala-city-tag">
                    <span style={{ color }}>📍</span> {city}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs && (
        <section className="sp-section">
          <div className="container">
            <motion.div className="section-header" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="badge">FAQ</div>
              <h2 className="section-title" style={{ marginTop: '12px' }}>
                Common Questions about <span className="gradient-text">{acronym}</span>
              </h2>
            </motion.div>
            <div className="faq-list">
              {faqs.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* RELATED SERVICES */}
      {relatedServices.length > 0 && (
        <section className="sp-section sp-section-alt">
          <div className="container">
            <h2 className="section-title" style={{ marginBottom: '32px' }}>
              Related <span className="gradient-text">Services</span>
            </h2>
            <div className="sp-related-grid">
              {relatedServices.map(s => (
                <Link key={s.name} to={s.path} className="sp-related-card">
                  <span className="sp-related-icon">{s.icon}</span>
                  <div>
                    <div className="sp-related-name">{s.name}</div>
                    <div className="sp-related-full">{s.full}</div>
                  </div>
                  <ArrowRight size={16} className="sp-related-arrow" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="sp-section">
        <div className="container">
          <motion.div className="cta-box" initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
            <div className="cta-glow"></div>
            <div className="badge"><Zap size={12} /> Free Consultation</div>
            <h2 className="cta-title" style={{ marginTop: '16px' }}>
              Start Your <span className="gradient-text">{acronym}</span> Journey Today
            </h2>
            <p className="cta-subtitle">
              Get a FREE {title} audit for your Kerala business. I'll analyze your current AI visibility and map out exactly what it takes to get you cited in AI answers.
            </p>
            <div className="cta-actions">
              <a href={`https://wa.me/917907038984?text=Hi%20Rajesh!%20I%20read%20the%20full%20${encodeURIComponent(acronym)}%20page%20on%20your%20site%20and%20I%E2%80%99m%20ready%20to%20get%20started.%20Please%20send%20me%20a%20free%20${encodeURIComponent(acronym)}%20audit%20for%20my%20business.`}
                target="_blank" rel="noreferrer" className="btn-primary" style={{ padding: '16px 32px', fontSize: '16px' }}>
                <Zap size={18} /> Get Free {acronym} Audit
              </a>
              <a href="tel:+917907038984" className="btn-secondary" style={{ padding: '15px 32px', fontSize: '16px' }}>
                Call Rajesh Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ServicePage;
